import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, formatAge, formatDateBR } from '../api'
import type { Exam, Patient, ReportTemplate, User } from '../api'

const EMPTY: Partial<Patient> = { name: '', birthDate: '', sex: 'F', cpf: '', phone: '', insurance: '' }

// Cadastro de pacientes ligado de verdade ao banco local (SQLite).
// Toda escrita entra na fila de sincronização automaticamente.
export function Pacientes({ user, onStartExam }: {
  user: User
  onStartExam: (examId: string, patientId: string, patientName: string) => void
}) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Patient[]>([])
  const [total, setTotal] = useState(0)
  const [draft, setDraft] = useState<Partial<Patient>>(EMPTY)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [examType, setExamType] = useState('')
  const [exams, setExams] = useState<Exam[]>([])
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    void api.templates.list().then((t) => {
      setTemplates(t.items)
      if (t.items[0]) setExamType(t.items[0].name)
    }).catch(() => undefined)
  }, [])

  const reload = useCallback(async (q: string) => {
    try {
      const out = await api.patients.list(q)
      setItems(out.items)
      setTotal(out.total)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar pacientes')
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void reload(query), 250)
    return () => clearTimeout(t)
  }, [query, reload])

  // histórico de exames do paciente selecionado
  useEffect(() => {
    if (!draft.id) { setExams([]); return }
    void api.patients.exams(draft.id).then((out) => setExams(out.items)).catch(() => setExams([]))
  }, [draft.id])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    try {
      if (draft.id) {
        const saved = await api.patients.update(draft as Patient)
        setDraft(saved)
        setNotice('Paciente atualizado.')
      } else {
        const saved = await api.patients.create(draft)
        setDraft(saved)
        setNotice('Paciente cadastrado.')
      }
      await reload(query)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao salvar')
    }
  }

  async function remove() {
    if (!draft.id) return
    if (!confirm(`Excluir ${draft.name}? O prontuário será ocultado (exclusão lógica auditada).`)) return
    try {
      await api.patients.remove(draft.id)
      setDraft(EMPTY)
      setNotice('Paciente excluído.')
      await reload(query)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao excluir')
    }
  }

  async function startExam() {
    if (!draft.id || !examType) return
    setStarting(true)
    setError('')
    try {
      const out = await api.exams.create({ patientId: draft.id, type: examType })
      onStartExam(out.exam.id, draft.id, draft.name ?? 'Paciente')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao iniciar exame')
    } finally {
      setStarting(false)
    }
  }

  const canWrite = user.role === 'admin' || user.role === 'medico' || user.role === 'recepcao'
  const canDelete = user.role === 'admin'

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '300px 1fr', minHeight: 0 }}>
      {/* lista */}
      <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 'none', padding: '9px 11px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6 }}>
          <input className="input" placeholder="Buscar por nome, CPF ou documento…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ flex: 1, height: 29 }} />
          <button className="btn btn-primary" style={{ height: 29, padding: '0 10px' }} onClick={() => { setDraft(EMPTY); setNotice(''); setError('') }}>
            Novo
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => { setDraft(p); setNotice(''); setError('') }}
              style={{
                width: '100%', textAlign: 'left', padding: '9px 11px', border: 'none',
                borderBottom: '1px solid var(--border)', cursor: 'pointer',
                background: draft.id === p.id ? 'var(--accent-soft)' : 'transparent',
              }}
            >
              <div style={{ font: '500 12.5px/1.3 var(--font-sans)' }}>{p.name}</div>
              <div style={{ marginTop: 2, font: '400 10.5px/1.3 var(--font-mono)', color: 'var(--text-2)' }}>
                {p.birthDate || '—'} · {p.cpf || p.doc || 'sem documento'}
              </div>
            </button>
          ))}
          {items.length === 0 && (
            <div style={{ padding: '18px 12px', textAlign: 'center', font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
              Nenhum paciente encontrado.
            </div>
          )}
        </div>
        <div style={{ flex: 'none', padding: '7px 11px', borderTop: '1px solid var(--border)', font: '400 10.5px/1 var(--font-mono)', color: 'var(--text-3)' }}>
          Exibindo {items.length} de {total} registros
        </div>
      </div>

      {/* formulário */}
      <div style={{ overflowY: 'auto', minHeight: 0, background: 'var(--surface)', padding: '14px 18px' }}>
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div style={{ font: '600 16px/1.2 var(--font-sans)' }}>{draft.id ? draft.name : 'Novo paciente'}</div>
            <div style={{ flex: 1 }} />
            {draft.id && (
              <>
                <select className="input" value={examType} onChange={(e) => setExamType(e.target.value)} style={{ height: 29, width: 190 }}>
                  {templates.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                  <option value="Retossigmoidoscopia">Retossigmoidoscopia</option>
                </select>
                <button type="button" className="btn btn-primary" onClick={() => void startExam()} disabled={starting} style={{ height: 29 }}>
                  {starting ? 'Iniciando…' : 'Iniciar captura'}
                </button>
                {canDelete && (
                  <button type="button" className="btn" onClick={() => void remove()} style={{ height: 29, color: 'var(--danger)' }}>
                    Excluir
                  </button>
                )}
              </>
            )}
            <button type="submit" className="btn btn-primary" disabled={!canWrite} style={{ height: 29, padding: '0 16px' }}>
              {draft.id ? 'Gravar' : 'Cadastrar'}
            </button>
          </div>

          {error && <div style={{ padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          {notice && <div style={{ padding: '8px 11px', border: '1px solid var(--ok)', borderRadius: 6, background: 'var(--ok-soft)', fontSize: 12 }}>{notice}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
            <label className="field"><span>Nome completo</span>
              <input className="input" value={draft.name ?? ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required minLength={3} />
            </label>
            <label className="field"><span>Nascimento</span>
              <input className="input" type="date" value={draft.birthDate ?? ''} onChange={(e) => setDraft({ ...draft, birthDate: e.target.value })} style={{ fontFamily: 'var(--font-mono)' }} />
            </label>
            <label className="field"><span>Sexo</span>
              <select className="input" value={draft.sex ?? 'F'} onChange={(e) => setDraft({ ...draft, sex: e.target.value })}>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
                <option value="NI">Não informado</option>
              </select>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <label className="field"><span>CPF</span>
              <input className="input" value={draft.cpf ?? ''} onChange={(e) => setDraft({ ...draft, cpf: e.target.value })} style={{ fontFamily: 'var(--font-mono)' }} />
            </label>
            <label className="field"><span>Documento</span>
              <input className="input" value={draft.doc ?? ''} onChange={(e) => setDraft({ ...draft, doc: e.target.value })} style={{ fontFamily: 'var(--font-mono)' }} />
            </label>
            <label className="field"><span>Telefone</span>
              <input className="input" value={draft.phone ?? ''} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} style={{ fontFamily: 'var(--font-mono)' }} />
            </label>
            <label className="field"><span>Convênio</span>
              <input className="input" value={draft.insurance ?? ''} onChange={(e) => setDraft({ ...draft, insurance: e.target.value })} />
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label className="field"><span>Observações clínicas</span>
              <textarea className="input" value={draft.notes ?? ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} style={{ height: 74, padding: '7px 8px', resize: 'none', fontFamily: 'var(--font-sans)', lineHeight: 1.5 }} />
            </label>
            <label className="field"><span>Alergias</span>
              <textarea className="input" value={draft.allergies ?? ''} onChange={(e) => setDraft({ ...draft, allergies: e.target.value })} style={{ height: 74, padding: '7px 8px', resize: 'none', fontFamily: 'var(--font-sans)', lineHeight: 1.5, borderColor: 'var(--danger)', background: 'var(--danger-soft)' }} />
            </label>
          </div>

          {draft.id && (
            <div style={{ marginTop: 6 }}>
              <div style={{ font: '600 11px/1 var(--font-sans)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
                Histórico de exames ({exams.length})
              </div>
              <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
                {exams.map((e) => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', borderBottom: '1px solid var(--border)', font: '400 12px/1.3 var(--font-sans)' }}>
                    <span className="badge" style={{ background: e.status === 'concluido' ? 'var(--ok-soft)' : e.status === 'aguardando_laudo' ? 'var(--primary-soft)' : 'var(--accent-soft)', color: e.status === 'concluido' ? 'var(--ok)' : e.status === 'aguardando_laudo' ? 'var(--primary)' : 'var(--accent)' }}>
                      {e.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span style={{ flex: 1 }}>{e.type}</span>
                    <span style={{ font: '400 11px/1 var(--font-mono)', color: 'var(--text-2)' }}>
                      {formatDateBR((e.startedAt ?? '').slice(0, 10))}
                    </span>
                    <span style={{ font: '400 11px/1 var(--font-mono)', color: 'var(--text-3)' }}>{formatAge(draft.birthDate)}</span>
                  </div>
                ))}
                {exams.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
                    Nenhum exame registrado para este paciente.
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
